import { FC, useEffect } from 'react'
import { RenderIf } from 'components/RenderIf'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { Button } from 'featureSliced/entities/Button'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { AmountHandler } from 'featureSliced/widgets/AmountHandler'
import { getAmountConfig } from 'featureSliced/shared/utils/weightSteps'
import { handleLocalStorage } from 'featureSliced/shared/hooks/handleLocalStorage'
import {
  getItemLeadDays,
  isItemLeadDaysExceeded,
} from 'featureSliced/shared/utils/itemLeadDays'

import { IProduct } from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { IMenuItemsBucket } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'
import { TableStatuses } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'

import {
  DeleteFromBucketButton,
  Divider,
  FooterActions,
  FooterContainer,
} from '../ComonProduct/styled'
import trashCan from 'assets/trashCan.svg'
import { KitchenInfoItemType } from '../types'
import { ProductTemplateIdType } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer/types'

type AddCommonProductFooterProps = {
  product: IProduct
  productTemplateId: ProductTemplateIdType
  amountState: [number, (value: number) => void]
  closeProduct: () => void
  selectedKitchenInfo: KitchenInfoItemType
  initAmount: number
  /** a template whose every step is fixed adds its components from here; the line
   * identity has to stay the one the picker produces, components included */
  menuItems?: IMenuItemsBucket[]
}

export const AddCommonProductFooter: FC<AddCommonProductFooterProps> = ({
  product,
  productTemplateId,
  amountState,
  closeProduct,
  selectedKitchenInfo,
  initAmount,
  menuItems,
}) => {
  // Hooks
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const addProductToBucket = QRMBucketSlice.actions.addProductToBucket
  const selectedQRMProducts = useMySelector((state) => state.bucket.products)
  const removeProductFromBucket = QRMBucketSlice.actions.removeProductFromBucket
  const { getProductPrice } = useGetProductPrice()

  // Store
  const tableStatus = useMySelector((state) => state.qrMenuTable.tableStatus)
  const bookingVariant = useMySelector((state) => state.app.menu.bookingVariant)
  const disabledByMobileWaiter = useMySelector(
    (state) => state.app.menu.disabledByMobileWaiter
  )

  // State
  const [currentAmount, setCurrentAmount] = amountState

  const componentIds = (menuItems || [])
    .map((component) => component.productId)
    .sort((a, b) => a - b)
  const uniqueId = `${productTemplateId}_${product.id}_${JSON.stringify(
    selectedKitchenInfo
  )}${menuItems ? `_${componentIds}` : ''}`
  const productFromBucket = selectedQRMProducts?.find(
    (item) => item.uniqueId === uniqueId
  )
  const isProductInBucket = Boolean(productFromBucket)
  const amountConfig = getAmountConfig(product.unitSellCode)
  const isLeadDaysExceeded = isItemLeadDaysExceeded({
    leadDays: getItemLeadDays(product.ClickCollectLeadDaysCount),
    pickUpTime: handleLocalStorage('pickUpTime'),
  })
  const clickCollectMin = product.ClickCollectOrderMin
  const hasClickCollectMin =
    clickCollectMin !== null && clickCollectMin !== undefined
  const defaultMinValue = hasClickCollectMin
    ? clickCollectMin
    : amountConfig.minAmount
  // never below the order minimum, in-bucket or not (QRM-649): removal is the
  // explicit delete button (weight) or the cart line's remove control, not count 0
  const minValue = defaultMinValue
  const weighed = product.unitSellCode === 2 || product.unitSellCode === 3

  // useEffect
  useEffect(() => {
    if (initAmount) {
      setCurrentAmount(initAmount)
    }
  }, [initAmount])

  useEffect(() => {
    if (!isProductInBucket && currentAmount < defaultMinValue) {
      setCurrentAmount(defaultMinValue)
    }
  }, [isProductInBucket, currentAmount, defaultMinValue, setCurrentAmount])

  const addToBucket = () => {
    const { id, categoryId, unitSellCode } = product
    const productToAdd = {
      uniqueId,
      productId: id,
      productTemplateId,
      categoryId,
      count: currentAmount,
      kitchenInfo: JSON.stringify(selectedKitchenInfo || {}),
      price: getProductPrice(product, productTemplateId).finalPrice,
      addedAsWeight: unitSellCode === 2 || unitSellCode === 3,
      ...(menuItems ? { menuItems } : {}),
    }
    dispatch(addProductToBucket(productToAdd))
    closeProduct()
  }

  const deleteFromBucket = () => {
    dispatch(removeProductFromBucket(uniqueId))
    closeProduct()
  }

  // No amount below the order minimum is selectable: a step that would land there
  // stops at the minimum. The old "step from the minimum drops to 0 → the button
  // flips to remove" path is retired (QRM-649).
  const handleDecreaseAmount = (value: number) => {
    setCurrentAmount(value < defaultMinValue ? defaultMinValue : value)
  }

  const handleIncreaseAmount = (value: number) => {
    setCurrentAmount(value < defaultMinValue ? defaultMinValue : value)
  }

  return (
    <RenderIf
      key={`${navigator.onLine}${bookingVariant}${disabledByMobileWaiter}${tableStatus}${product.soldOut}${isLeadDaysExceeded}`}
      condition={
        navigator.onLine &&
        (bookingVariant === 2 || process.env.REACT_APP_IS_CLIC === '1') &&
        !disabledByMobileWaiter &&
        tableStatus !== TableStatuses.BUSY &&
        !product.soldOut &&
        !isLeadDaysExceeded
      }
    >
      <Divider stacked={Boolean(amountConfig.coarseStep)} />
      <FooterContainer stacked={Boolean(amountConfig.coarseStep)}>
        <AmountHandler
          multiplier={amountConfig.multiplier}
          step={amountConfig.step}
          coarseStep={amountConfig.coarseStep}
          fineLabel={amountConfig.fineLabel}
          coarseLabel={amountConfig.coarseLabel}
          unit={amountConfig.unit}
          currentValue={currentAmount}
          onDecrease={handleDecreaseAmount}
          onIncrease={handleIncreaseAmount}
          minValue={minValue} // only needed for C&C
          maxValue={product.ClickCollectOrderMax ?? undefined} // only needed for C&C
        />
        {weighed && isProductInBucket ? (
          <FooterActions stacked={Boolean(amountConfig.coarseStep)}>
            <DeleteFromBucketButton
              type="button"
              aria-label={t('buttons.remove')}
              title={t('buttons.remove')}
              onClick={deleteFromBucket}
            >
              <img src={trashCan} alt="" />
            </DeleteFromBucketButton>
            <Button
              buttonType="common"
              disabled={tableStatus === TableStatuses.BUSY}
              width="100%"
              onClick={addToBucket}
            >
              {t('products.addOrder')}
            </Button>
          </FooterActions>
        ) : (
          <Button
            buttonType="common"
            disabled={tableStatus === TableStatuses.BUSY}
            width="100%"
            onClick={addToBucket}
          >
            {t('products.addOrder')}
          </Button>
        )}
      </FooterContainer>
    </RenderIf>
  )
}
